import { motion } from 'framer-motion';
import { Code2, Server, Cloud, Handshake } from 'lucide-react';
import { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { DATA_FR } from '../data/fr';
import { LABELS } from '../data/labels';
import { getSkills } from '../services/skillsApi';
import type { Skill } from '../services/skillsApi';

const SkillPill = ({ name, idx, variant = "primary" }: { name: string, idx: number, variant?: "primary" | "tertiary" | "secondary" }) => {
  const variantClasses = {
    primary: "bg-primary-container text-on-primary-container hover:bg-primary hover:text-on-primary",
    tertiary: "bg-tertiary-container text-on-tertiary-container hover:bg-tertiary hover:text-on-tertiary",
    secondary: "bg-secondary-container text-on-secondary-container hover:bg-secondary hover:text-on-secondary"
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 10 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.05, type: "spring", stiffness: 200, damping: 20 }}
      className={`flex items-center px-5 py-3 rounded-full ${variantClasses[variant]} hover:shadow-elevation-1 transition-all duration-300 cursor-default`}
    >
      <span className="text-[15px] font-medium tracking-wide">{name}</span>
    </motion.div>
  );
};

const TechnicalSkills = ({ languages, infrastructure, cloud, labels }: { languages: any[], infrastructure: any[], cloud: any[], labels: typeof LABELS['fr'] }) => (
  <div className="card card-hover p-8 mb-8">
    <div className="flex items-center gap-3 mb-8">
      <div className="p-3 bg-primary-container rounded-xl">
        <Code2 className="w-6 h-6 text-on-primary-container" />
      </div>
      <h2 className="text-2xl font-bold text-on-surface">{labels.technical_skills}</h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
      <div>
        <h3 className="flex items-center gap-2 font-bold text-lg text-primary mb-4">
          <Code2 className="w-5 h-5" />
          {labels.languages}
        </h3>
        <div className="flex flex-wrap gap-3">
          {languages.map((skill, idx) => (
            <SkillPill key={skill.name} name={skill.name} idx={idx} variant="primary" />
          ))}
        </div>
      </div>

      <div>
        <h3 className="flex items-center gap-2 font-bold text-lg text-tertiary mb-4">
          <Server className="w-5 h-5" />
          {labels.infrastructure}
        </h3>
        <div className="flex flex-wrap gap-3">
          {infrastructure.map((skill, idx) => (
            <SkillPill key={skill.name} name={skill.name} idx={idx} variant="tertiary" />
          ))}
        </div>
      </div>

      <div>
        <h3 className="flex items-center gap-2 font-bold text-lg text-secondary mb-4">
          <Cloud className="w-5 h-5" />
          {labels.cloud_tools}
        </h3>
        <div className="flex flex-wrap gap-3">
          {cloud.map((skill, idx) => (
            <SkillPill key={skill.name} name={skill.name} idx={idx} variant="secondary" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const SoftSkills = ({ soft, labels }: { soft: any[], labels: typeof LABELS['fr'] }) => (
  <div className="card card-hover p-8">
    <div className="flex items-center gap-3 mb-8">
      <div className="p-3 bg-tertiary-container rounded-[16px]">
        <Handshake className="w-6 h-6 text-on-tertiary-container" />
      </div>
      <h2 className="text-2xl font-bold text-on-surface">{labels.soft_skills}</h2>
    </div>

    <div className="flex flex-wrap gap-3">
      {soft.map((skill, idx) => {
        const IconComponent = skill.icon_name ? (LucideIcons as any)[skill.icon_name] : skill.icon;

        return (
          <motion.div
            key={skill.name}
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.05, type: "spring", stiffness: 200, damping: 20 }}
            className="flex items-center px-5 py-3 rounded-full bg-secondary-container text-on-secondary-container hover:bg-secondary hover:text-on-secondary hover:shadow-elevation-1 transition-all duration-300 cursor-default"
          >
            {IconComponent && <IconComponent className="w-4 h-4 mr-2" />}
            <span className="text-[15px] font-medium tracking-wide">{skill.name}</span>
          </motion.div>
        );
      })}
    </div>
  </div>
);

export function SkillsSection({ data, labels }: { data: typeof DATA_FR, labels: typeof LABELS['fr'] }) {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const fetched = await getSkills();
        if (fetched && fetched.length > 0) {
          setSkills(fetched);
        } else {
          // Flatten static fallback to match API format roughly
          const staticSkills = [
            ...data.skills.languages.map(s => ({ ...s, category: 'languages' })),
            ...data.skills.infrastructure.map(s => ({ ...s, category: 'infrastructure' })),
            ...data.skills.cloud.map(s => ({ ...s, category: 'cloud' })),
            ...data.skills.soft.map(s => ({ ...s, category: 'soft' }))
          ];
          setSkills(staticSkills as any);
        }
      } catch (e) {
        console.error('Erreur chargement skills', e);
        const staticSkills = [
          ...data.skills.languages.map(s => ({ ...s, category: 'languages' })),
          ...data.skills.infrastructure.map(s => ({ ...s, category: 'infrastructure' })),
          ...data.skills.cloud.map(s => ({ ...s, category: 'cloud' })),
          ...data.skills.soft.map(s => ({ ...s, category: 'soft' }))
        ];
        setSkills(staticSkills as any);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [data.skills]);

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="card p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[1, 2, 3].map(col => (
              <div key={col}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {[1, 2, 3, 4].map(skill => (
                    <div key={skill} className="px-5 py-3 rounded-full bg-gray-200 h-10 w-24"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gray-200 rounded-[16px]"></div>
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          </div>
          <div className="flex flex-wrap gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="px-5 py-3 rounded-full bg-gray-200 h-10 w-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const languages = skills.filter(s => s.category === 'languages');
  const infrastructure = skills.filter(s => s.category === 'infrastructure');
  const cloud = skills.filter(s => s.category === 'cloud');
  const soft = skills.filter(s => s.category === 'soft');

  return (
    <div className="space-y-8">
      <TechnicalSkills
        languages={languages}
        infrastructure={infrastructure}
        cloud={cloud}
        labels={labels}
      />
      <SoftSkills soft={soft} labels={labels} />
    </div>
  );
}
